import * as THREE from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import {UnrealBloomPass} from "three/examples/jsm/postprocessing/UnrealBloomPass";
import {ShaderPass} from "three/examples/jsm/postprocessing/ShaderPass";
import {FXAAShader} from "three/examples/jsm/shaders/FXAAShader";

class ThreeWrapperOptions {
  backgroundColor = 0xffffff
  fov = 45
  cameraLookAtVector = new THREE.Vector3(0,0,0)
  cameraFilmGauge = 35
  rendererShadowMapEnabled = true
  rendererOutputEncoding = THREE.sRGBEncoding
}

const VERTEX_SHADER = 'varying vec2 vUv;\n' +
  '\n' +
  '\t\t\tvoid main() {\n' +
  '\n' +
  '\t\t\t\tvUv = uv;\n' +
  '\n' +
  '\t\t\t\tgl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );\n' +
  '\n' +
  '\t\t\t}'

const FRAGMEN_SHADER = 'uniform sampler2D baseTexture;\n' +
  '\t\t\tuniform sampler2D bloomTexture;\n' +
  '\n' +
  '\t\t\tvarying vec2 vUv;\n' +
  '\n' +
  '\t\t\tvoid main() {\n' +
  '\n' +
  '\t\t\t\tgl_FragColor = ( texture2D( baseTexture, vUv ) + vec4( 1.0 ) * texture2D( bloomTexture, vUv ) );\n' +
  '\n' +
  '\t\t\t}'

class ThreeWrapper {

  static BLOOM_SCENE = 1

  _options = new ThreeWrapperOptions()

  /**
   *
   * @type {EffectComposer}
   */
  _composer = null
  _fxaaPass = null

  /**
   *
   * @type {EffectComposer}
   */
  _defComposer = null
  /**
   *
   * @type {Scene}
   */
  _scene = null
  envMap = null
  /**
   *
   * @type {WebGLRenderer}
   */
  _renderer = null
  /**
   *
   * @type {PerspectiveCamera}
   */
  _camera = null
  /**
   *
   * @type {boolean}
   */

  _animated = null


  _highAntialias = false
  _prevCameraPosition = new THREE.Vector3()
  _prevCameraRotation = new THREE.Quaternion()
  _prevFOV = 0
  renderAlways = false

  /**
   *
   * @type {HTMLElement}
   */
  _wrapElement = null
  _sceneElement = null

  /**
   *
   * @type {Array}
   * @private
   */
  _updateControllers = []

  _bloomEnabled = true
  _bloomComposers = []
  _bloomedMaterials = {}
  _bloomDarkMaterial = new THREE.MeshBasicMaterial( { color: "black"} )
  _bloomDartSpiteMaterial = new THREE.SpriteMaterial( { color: "black", depthTest: true } )

  _carLightsEnabled = false
  _carAmbientLightsEnabled = false

  _lights = []

  constructor(wrapElement, {
    backgroundColor,
    fov,
    cameraLookAtVector,
    cameraFilmGauge,
    rendererShadowMapEnabled,
    rendererOutputEncoding
  }) {
    this._wrapElement = wrapElement
    backgroundColor !== undefined ? this._options.backgroundColor = backgroundColor : null
    fov !== undefined ? this._options.fov = fov : null
    cameraLookAtVector !== undefined ? this._options.cameraLookAtVector = cameraLookAtVector : null
    cameraFilmGauge !== undefined ? this._options.cameraFilmGauge = cameraFilmGauge : null
    rendererShadowMapEnabled !== undefined ? this._options.rendererShadowMapEnabled = rendererShadowMapEnabled : null
    rendererOutputEncoding !== undefined ? this._options.rendererOutputEncoding = rendererOutputEncoding : null

    this.init()
  }

  init() {
    this._sceneElement = document.querySelectorAll('.three-js-scene')[0]
    let sceneRect = this._sceneElement.getBoundingClientRect()

    this._scene = new THREE.Scene()
    this._scene.background = new THREE.Color(this._options.backgroundColor)

    this._camera = new THREE.PerspectiveCamera( this._options.fov, sceneRect.width / sceneRect.height, 0.01, 1000 )
    // this._scene.add(this.camera)
    this._camera.position.set(0, 5, -20)
    this._camera.lookAt(this._options.cameraLookAtVector)
    this._camera.filmGauge = this._options.cameraFilmGauge

    this._renderer = new THREE.WebGLRenderer( { antialias: false, powerPreference: 'high-performance' } )
    this._renderer.autoClear = false
    this._renderer.setPixelRatio(1.7)
    this._renderer.setSize( sceneRect.width, sceneRect.height)
    this._renderer.shadowMap.enabled = this._options.rendererShadowMapEnabled
    this._renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this._renderer.outputEncoding = this._options.rendererOutputEncoding
    this._renderer.physicallyCorrectLights = true

    this._renderer.toneMapping = THREE.ReinhardToneMapping

    this._wrapElement.appendChild(this._renderer.domElement)

    let renderScene = new RenderPass( this.scene, this.camera );
    this._renderer.toneMappingExposure = Math.pow( 1.2, 4.0 )

    let finalPassList = this.createBloomComposers(renderScene)

    this._fxaaPass = new ShaderPass( FXAAShader )
    let pixelRatio = 2
    this._fxaaPass.material.uniforms[ 'resolution' ].value.x = 1 / ( sceneRect.width * pixelRatio )
    this._fxaaPass.material.uniforms[ 'resolution' ].value.y = 1 / ( sceneRect.height * pixelRatio )

    this._composer = new EffectComposer( this._renderer )
    this._composer.addPass( renderScene )
    finalPassList.forEach(pass => this._composer.addPass(pass))
    this._composer.addPass( this._fxaaPass )

    this._defComposer = new EffectComposer( this._renderer )
    this._defComposer.addPass( renderScene )
    // this._defComposer.addPass( this._fxaaPass )

    // let geometry = new THREE.BoxGeometry( 0.5, 0.5, 0.5)
    // let material = new THREE.MeshBasicMaterial( {color: 0xff0000} )
    // let cube = new THREE.Mesh( geometry, material )
    // cube.visible = true
    // this._scene.add(cube)
    // cube.position.set(0,0,0)

    // let light = new THREE.AmbientLight(0xffffff, 1)
    // this.scene.add(light)

    this._animated = true
    this.runAnimation()
  }

  createBloomComposers(renderScene) {
    let finalPassList = []

    let bloomOptions = [
      // {
      //   threshold: 0,
      //   strength: 0.01,
      //   radius: 1
      // },
      {
        threshold: 0,
        strength: 4,
        radius: 1
      },
      // {
      //   threshold: 0,
      //   strength: 0.02,
      //   radius: 1
      // }
    ]

    for (let i = 0; i < bloomOptions.length; i++) {
      const configurated = {
        pass: new UnrealBloomPass( new THREE.Vector2( window.innerWidth, window.innerHeight ), 1.5, 0.4, 0.85),
        composer: new EffectComposer( this._renderer ),
        layers: new THREE.Layers()
      }

      configurated.layers.set(i + 1)


      configurated.pass.threshold = bloomOptions[i].threshold
      configurated.pass.strength = bloomOptions[i].strength
      configurated.pass.radius = bloomOptions[i].radius

      configurated.composer.renderToScreen = false
      configurated.composer.addPass(renderScene)
      configurated.composer.addPass(configurated.pass)

      const finalPass = new ShaderPass(
        new THREE.ShaderMaterial( {
          uniforms: {
            baseTexture: { value: null },
            bloomTexture: { value: configurated.composer.renderTarget2.texture }
          },
          vertexShader: VERTEX_SHADER,
          fragmentShader: FRAGMEN_SHADER,
          defines: {}
        } ), "baseTexture"
      );
      finalPass.needsSwap = true

      finalPassList.push(finalPass)

      this._bloomComposers.push(configurated)
    }

    return finalPassList
  }


  runAnimation() {
    if (this._animated) {
      requestAnimationFrame(() => {
        this.runAnimation()
      })

      let someUpdated = false
      this._updateControllers.forEach(item => {
        item.update()
        if (item.someUpdated) {
          item.someUpdated = false
          someUpdated = true
        }
      })

      if (this._prevCameraPosition) {
        if (this.renderAlways ||
          this.camera.fov.toFixed(2) !== this._prevFOV.toFixed(2) ||
          this.camera.position.distanceTo(this._prevCameraPosition).toFixed(4) > 0 ||
          this.camera.quaternion.angleTo(this._prevCameraRotation).toFixed(4) > 0) {
          someUpdated = true
        }
      }

      this._prevCameraPosition.set(this.camera.position.x, this.camera.position.y, this.camera.position.z)
      this._prevCameraRotation.set(this.camera.quaternion.x, this.camera.quaternion.y, this.camera.quaternion.z, this.camera.quaternion.w)
      this._prevFOV = this.camera.fov

      if (someUpdated)  {
        this.render()
      }
    }
  }

  addLight(light) {
    this._lights.push(light)
    this._scene.add(light)
  }

  loadHDR(url) {
    // const pmremGenerator = new THREE.PMREMGenerator( this._renderer )
    // const loader = new RGBELoader()
    // loader.setDataType(THREE.UnsignedByteType).load(url, (texture) => {
    //   var envMap = pmremGenerator.fromEquirectangular( texture ).texture;
    //   envMap.format = THREE.sRGBEncoding
    //
    //   this.scene.background = envMap;
    //   this.scene.environment = envMap;
    //
    //   texture.dispose();
    //   pmremGenerator.dispose();
    // })

    return new Promise((resolve) => {
      const pmremGenerator = new THREE.PMREMGenerator( this._renderer )
      pmremGenerator.compileEquirectangularShader()

      const rgbEloader = new RGBELoader()
      rgbEloader.setDataType( THREE.UnsignedByteType ).load( url, ( texture ) => {
        let envMap = pmremGenerator.fromEquirectangular( texture ).texture;
        this.envMap = envMap;
        // this.scene.environment = envMap

        // texture.dispose();
        pmremGenerator.dispose();
        resolve()
      })
    })
  }

  addUpdateController(controller) {
    if (this._updateControllers.indexOf(controller) < 0) {
      this._updateControllers.push(controller)
    }
  }

  removeUpdateController(controller) {
    this._updateControllers = this._updateControllers.filter(item => item !== controller)
  }

  setBoomEnabled (enabled) {
    this._bloomEnabled = enabled
  }

  renderBloom () {
    this._bloomComposers.forEach(composerData => {
      this.scene.traverse(obj => {
        if ((obj.name === 'FARA_GLASS' ||
          obj.name === 'GLASS_BAGAZH' ||
          obj.name === 'FARA_back_GLASS' ||
          obj.name === 'FARA_back_GLASS001'
        ) && this._carLightsEnabled) obj.visible = false
        if (obj.name === 'L1_BACKLIGHT') obj.visible = true

        // if (composerData.layers.test( obj.layers )) console.log(obj.name, obj.material && composerData.layers.test( obj.layers ))

        if ( obj.material && composerData.layers.test( obj.layers ) === false ) {
          this._bloomedMaterials[ obj.uuid ] = obj.material
          obj.material = obj.type === 'Sprite' ? this._bloomDartSpiteMaterial : this._bloomDarkMaterial
          if (obj.type === 'Sprite') {
            obj.visible = false
          }
        }
      })

      composerData.composer.render()

      this.scene.traverse(obj => {
        if (obj.name === 'FARA_GLASS' ||
          obj.name === 'GLASS_BAGAZH' ||
          obj.name === 'FARA_back_GLASS' ||
          obj.name === 'FARA_back_GLASS001'
        ) obj.visible = true
        if (obj.name === 'L1_BACKLIGHT') obj.visible = false

        if ( this._bloomedMaterials[ obj.uuid ] ) {
          obj.material = this._bloomedMaterials[ obj.uuid ]
          if (obj.type === 'Sprite') {
            obj.visible = true
          }
          delete this._bloomedMaterials[ obj.uuid ]
        }
      })

      this._bloomedMaterials = {}

    })
  }

  render() {
    if (this._composer) {
      // this._bloomEnabled = false
      if (this._bloomEnabled) {
        this.renderBloom()
        this._composer.render()
      } else {
        this._defComposer.render()
      }
    }
    // if (this._renderer) this._renderer.render(this.scene, this.camera)
  }
  
  updateProjectionMatrix() {
    if (this._renderer) {
      setTimeout(() => {
        let sceneRect = this._sceneElement.getBoundingClientRect()

        this._camera.aspect = sceneRect.width / sceneRect.height
        this._camera.updateProjectionMatrix()

        this._renderer.setSize(sceneRect.width, sceneRect.height)
        this.render()
      }, 10)
    }
  }

  /**
   *
   * @returns {PerspectiveCamera}
   */
  get camera () {
    return this._camera
  }

  /**
   *
   * @returns {THREE.Scene}
   */
  get scene () {
    return this._scene
  }
  get renderer() {
    return this._renderer
  }

  dispose() {
    this._animated = false;
    this._renderer.dispose();
    this._scene.dispose();
    this._scene = null;
  }
}

export default ThreeWrapper